<template>
	<div class="map-tooltip-style legend-position">
		<Spin :spinning="drawModeRef === CCM_MODE.loading">
			<VerticalLayout class="pd-vertical">
				<TypographyTitle :level="3">{{ $t('cnet-constructor.legend.title') }}</TypographyTitle>
				<TypographyParagraph type="secondary">{{ $t('cnet-constructor.legend.description') }}</TypographyParagraph>
				<Form :model="formState" :label-col="{ span: 9 }" :wrapper-col="{ span: 15 }" :colon="false" labelWrap>
					<FormItem :label="$t('cnet-constructor.legend.titleLabel')" v-bind="validateInfos.title">
						<InputRestricted v-model="titleRef" />
					</FormItem>
					<FormItem :label="$t('cnet-constructor.legend.stepLabel')" v-bind="validateInfos.step">
						<InputDecimal v-model="stepRef" :minValue="1" :maxValue="1000" :allowPoint="true" />
					</FormItem>
					<LabeledSwitch
						class="pd-md-vertical"
						small
						v-model="showOtherGridsRef"
						:rightLabel="$t('cnet-constructor.legend.showOtherGridsLabel')"
					/>
				</Form>
				<Steps direction="vertical" size="small" v-model:current="drawModeRef">
					<Step :title="$t('cnet-constructor.legend.step1Title')">
						<template #description>
							<div>{{ $t('cnet-constructor.legend.step1Description1') }}</div>
							<i18n-t keypath="cnet-constructor.legend.step1Description2" tag="div" scope="global">
								<template #keyStroke>
									<TypographyText keyboard>{{ $t('cnet-constructor.legend.step1DescriptionKeyStroke') }}</TypographyText>
								</template>
							</i18n-t>
						</template>
					</Step>
					<Step :title="$t('cnet-constructor.legend.step2Title')" :description="$t('cnet-constructor.legend.step2Description')" />
					<Step :title="$t('cnet-constructor.legend.step3Title')">
						<template #description>
							<div v-if="showSubgridsUi && drawModeRef === CCM_MODE.selectGridpoints">{{ $t('cnet-constructor.legend.step3Description1') }}</div>
							<Space v-if="showSubgridsUi && drawModeRef === CCM_MODE.selectGridpoints" size="small" class="pd-vertical">
								<Select v-model:value="currentSubGridRef" :options="subGridsOptions" size="small" />
								<Button @click="addSubGrid" size="small" shape="circle">
									<template #icon>
										<PlusOutlined />
									</template>
								</Button>
								<Button @click="deleteSubGrid" :disabled="!allowDeleteSubGrid" size="small" shape="circle">
									<template #icon>
										<MinusOutlined />
									</template>
								</Button>
							</Space>
							<div>{{ $t('cnet-constructor.legend.step3Description2') }}</div>
							<VerticalLayout v-if="drawModeRef === CCM_MODE.selectGridpoints" size="small">
								<Badge :status="selectedDataRef.status" :text="selectedDataRef.text" />
								<Space size="small">
									<Button size="small" @click="updateAllGridpointsStatuses(true)">{{ $t('cnet-constructor.legend.selectAllGridpoints') }}</Button>
									<Button size="small" @click="updateAllGridpointsStatuses(false)">{{ $t('cnet-constructor.legend.deselectAllGridpoints') }}</Button>
								</Space>
							</VerticalLayout>
						</template>
					</Step>
				</Steps>
				<Button type="primary" :disabled="!formIsValid" @click="onSaveModalOpen">{{ $t('cnet-constructor.legend.saveAndApplyButton') }}</Button>
			</VerticalLayout>
		</Spin>
	</div>
	<ApplyCNetModal
		v-model:visible="saveModalVisible"
		:forcedSubNetworkId="0"
		:cNetConstructorSaveAndApply="cNetConstructorSaveAndApply"
		:subGridCount="subGridCount"
		:subGridSelectedCounts="subGridSelectedCounts"
		:saveEnabled="true"
	/>
</template>

<script>
import { computed, toRef, onMounted, ref } from 'vue';
import { useVModel } from '@vueuse/core';

import {
	TypographyTitle, TypographyText, TypographyParagraph,
	Badge, Button, Steps, Step, Space, Spin, Form, FormItem, Select,
} from 'ant-design-vue';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons-vue';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import InputDecimal from '@/components/common/InputDecimal.vue';
import InputRestricted from '@/components/common/InputRestricted.vue';
import LabeledSwitch from '@/components/common/LabeledSwitch.vue';

import ApplyCNetModal from '@/components/map/cnetConstructorMode/ApplyCNetModal.vue';

import Validators from '@/api/Validators';

import { CCM_MODE } from '@/components/map/cnetConstructorMode/ccmConsts';


export default {
	components: {
		TypographyTitle, TypographyText, TypographyParagraph, Badge,
		Button, Steps, Step, Space, Spin, Form, FormItem, Select,
		PlusOutlined, MinusOutlined,
		VerticalLayout, InputDecimal, InputRestricted,
		ApplyCNetModal,
		LabeledSwitch,
	},
	props: {
		drawMode: {
			type: Number,
			required: true,
		},
		step: {
			type: [String, Number],
			required: true,
		},
		title: {
			type: String,
			required: true,
		},
		/*
		{
			status: 'success' | 'error',
			text: string,
		}
		*/
		selectedData: {
			type: Object,
			required: true,
		},
		editMode: {
			type: Boolean,
			required: true,
		},
		updateAllGridpointsStatuses: {
			type: Function,
			required: true,
		},
		cNetConstructorSaveAndApply: {
			type: Function,
			required: true,
		},
		subGridCount: {
			type: Number,
			required: true,
		},
		subGridSelectedCounts: {
			type: Array,
			default: () => [],
		},
		currentSubGrid: {
			type: Number,
			required: true,
		},
		allowDeleteSubGrid: {
			type: Boolean,
			required: true,
		},
		subGridsOptions: {
			type: Array,
			required: true,
		},
		addSubGrid: {
			type: Function,
			required: true,
		},
		deleteSubGrid: {
			type: Function,
			required: true,
		},
		showSubgridsUi: {
			type: Boolean,
			required: true,
		},
		showOtherGrids: {
			type: Boolean,
			required: true,
		},
	},
	setup(props, { emit }) {
		const titleRef = useVModel(props, 'title', emit);
		const stepRef = useVModel(props, 'step', emit);
		const drawModeRef = useVModel(props, 'drawMode', emit);
		const editModeRef = useVModel(props, 'editMode', emit);
		const currentSubGridRef = useVModel(props, 'currentSubGrid', emit);
		const showOtherGridsRef = useVModel(props, 'showOtherGrids', emit);

		const selectedDataRef = toRef(props, 'selectedData');

		const formState = computed(() => ({
			title: titleRef.value,
			step: stepRef.value,
		}));
		const { validate, validateInfos } = Validators.useMapLegendFormRules(formState);
		const formIsValid = computed(() => {
			return selectedDataRef.value.status === 'success' &&
				validateInfos.title.validateStatus === 'success' &&
				validateInfos.step.validateStatus === 'success'
		});
		onMounted(() => {
			validate();
		});

		const saveModalVisible = ref(false);
		const onSaveModalOpen = () => {
			saveModalVisible.value = true;
		}
		
		return {
			formState,
			validateInfos,
			stepRef,
			drawModeRef,
			titleRef,
			editModeRef,
			formIsValid,
			selectedDataRef,
			CCM_MODE,

			currentSubGridRef,
			showOtherGridsRef,

			saveModalVisible,
			onSaveModalOpen,
		};
	},
}
</script>

<style scoped>
@import '@/assets/map-legend.css';
</style>
